import { css } from "@linaria/core";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslations } from "../../../../../../../../../api/useTranslations";
import t from "../../../../../../../../../i18n";
import { uniqBy } from "diagnostics-lib/array";
import Checkbox from "@ui/Checkbox";
import Select from "@ui/Select";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";

import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import { type BlockDiagramModelT } from "../../../../../../schema/block/diagram";
import type { FaultColumnsT, QuantityColumnsT } from "../../../../../../schema/resource/columns";
import { getAvailableResourceKind } from "../../../../../../utils/getAvailableResourceKind";
import { AnnotationLayerContentSettings } from "./LayerContent";
import { AnnotationLayerNameSettings } from "./LayerName";
import { AnnotationLayerThresholdsSettings } from "./LayerThresholds";
import {
  AGGREGATE_NO_SYSTEM,
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../../hook/useAvailableResources";
import { AnnotationLayerEvolutionsSettings } from "./LayerEvolutions";
import Switch from "@ui/Switch";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";

const QUANTITY_COLUMN: QuantityColumnsT[] = [
  "measured",
  "std",
  "expected",
  "gap",
  "validity",
  "normalizedGap",
];

const DISABLED_MODEL_OUTPUT_COLUMNS = new Set<QuantityColumnsT>(["expected", "gap"]);

const isModelOutputColumn = (key: string): key is QuantityColumnsT =>
  DISABLED_MODEL_OUTPUT_COLUMNS.has(key as QuantityColumnsT);

const FAULT_COLUMN: FaultColumnsT[] = ["faultStatus", "real"];

const INCLUDED_FAULTS = [
  { label: t("project.dashboard.fault.status.to_analyse"), value: "TO_BE_ANALYSED" },
  { label: t("project.dashboard.fault.status.suspected"), value: "SUSPECTED" },
  { label: t("project.dashboard.fault.status.confirmed"), value: "CONFIRMED" },
  { label: t("project.dashboard.fault.status.false_detection"), value: "FORCED_FALSE" },
  { label: t("project.dashboard.fault.non_modelled"), value: "NON_MODELLED" },
];

export const AnnotationSettings = memo(
  ({
    projectId,
    block,
    annotation,
  }: {
    projectId: string;
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
  }) => {
    const editor = useDashboardEditorContext();
    const availableResources = useAvailableResources({ projectId });
    const translations = useTranslations();

    const selectedIdentifier = useMemo(
      () => annotation.encodings[0]?.identifier ?? null,
      [annotation.encodings],
    );

    const isSelectedIdentifierUsedForExternalGap = useMemo(
      () =>
        Boolean(
          availableResources.quantity?.identifiers.find(
            identifier =>
              selectedIdentifier === identifier.name &&
              "usedForExternalGaps" in identifier &&
              identifier.usedForExternalGaps,
          ),
        ),
      [availableResources.quantity?.identifiers, selectedIdentifier],
    );

    const isSelectedIdentifierUsedForSimulationOutputs = useMemo(
      () =>
        Boolean(
          availableResources.quantity.identifiers.find(
            identifier =>
              selectedIdentifier === identifier.name && identifier.usedForSimulationOutputs,
          ),
        ),
      [availableResources.quantity.identifiers, selectedIdentifier],
    );

    const isSelectedIdentifierFormulaQuantity = useMemo(
      () =>
        Boolean(
          availableResources.quantity.identifiers.find(
            identifier => selectedIdentifier === identifier.name && identifier.isFormula,
          ),
        ),
      [availableResources.quantity.identifiers, selectedIdentifier],
    );

    const isSelectedIdentifierManualFailure = useMemo(
      () =>
        availableResources.fault.identifiers.find(
          identifier => selectedIdentifier === identifier.name,
        )?.manual,
      [availableResources.fault.identifiers, selectedIdentifier],
    );

    const selectedSystemFaultStatus = useMemo(() => {
      if (!block || !selectedIdentifier) return [];

      const systemTransforms = block.data.transforms?.filter(t =>
        t.filter?.some(f => f.value.includes(selectedIdentifier)),
      );

      return (
        systemTransforms?.flatMap(
          t =>
            t.filter
              ?.filter(f => f.column === "faultStatus")
              .flatMap(f => (Array.isArray(f.value) ? f.value.map(String) : [String(f.value)])) ??
            [],
        ) ?? []
      );
    }, [block, selectedIdentifier]);

    const currentKind = useMemo(
      () => getAvailableResourceKind(availableResources, selectedIdentifier ?? ""),
      [availableResources, selectedIdentifier],
    );

    const isSelectedSystemKind = currentKind === "SYSTEM";
    const isSelectedUnexplainedLosses = selectedIdentifier === AGGREGATE_UNEXPLAINED_LOSSES;

    const encodingOptions = useMemo(() => {
      if (currentKind === "QUANTITY") {
        const isObservable = availableResources.observables.some(
          o => o.name === selectedIdentifier,
        );
        return [
          ...QUANTITY_COLUMN.filter(
            c => c !== "validity" && (isObservable ? true : c !== "normalizedGap"),
          ).map(c => ({
            label: t("project.dashboard.resource.column.annotation", c),
            value: c,
          })),
          ...availableResources.performanceIndicators.map(pi => ({
            label: `${t("project.dashboard.resource.column.annotation", "externalImpacts")} ${pi?.name}`,
            value: `externalImpacts/${pi?.name}`,
          })),
        ];
      }

      if (currentKind === "FAULT") {
        return [
          ...FAULT_COLUMN.filter(c => c !== "faultStatus").map(c => ({
            label: t("project.dashboard.resource.column.annotation", c),
            value: c,
          })),
          ...availableResources.performanceIndicators.map(pi => ({
            label: `${t("project.dashboard.resource.column.annotation", "quantityImpacts")} ${pi?.name}`,
            value: `quantityImpacts/${pi?.name}`,
          })),
        ];
      }

      if (currentKind === "SYSTEM") {
        return availableResources.performanceIndicators.map(pi => ({
          label: `${pi?.name}`,
          value: `systemImpact/${pi?.name}`,
        }));
      }

      if (currentKind === "AGGREGATE") {
        return availableResources.performanceIndicators.map(pi => ({
          label: `${pi?.name}`,
          value: `unexplainedLosses/${pi?.name}`,
        }));
      }

      return [];
    }, [
      availableResources.observables,
      availableResources.performanceIndicators,
      currentKind,
      selectedIdentifier,
    ]);

    const updateShowInfo = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!block) return;

        const updatedLayers = block.config.layers.map(layer => {
          if (layer !== annotation) return layer;
          return {
            ...layer,
            showDescription:
              e.target.name === "showLabel" && !e.target.checked ? false : layer.showDescription,
            [e.target.name]: e.target.checked ?? false,
          };
        });

        void editor?.updateBlock({
          ...block,
          config: { ...block.config, layers: updatedLayers },
        });
      },
      [block, editor, annotation],
    );

    const activePowerQuantity = useMemo(
      () => availableResources.performanceIndicators.find(pi => pi.isActivePower) ?? null,
      [availableResources.performanceIndicators],
    );
    const hasActivePowerQuantitySelected = useMemo(
      () =>
        annotation?.encodings.some(
          e =>
            (e.column === "systemImpact" || e.column === "noSystemImpacts") &&
            e.quantity === activePowerQuantity?.name,
        ),
      [annotation, activePowerQuantity],
    );

    const distributionByEventMode = useMemo(() => {
      if (!block) return false;

      const systemTransform = (block.data.transforms ?? []).find(t =>
        t.filter?.every(
          filter =>
            filter.column === "faultStatus" ||
            (filter.column === "systemId" && filter.value.includes(selectedIdentifier)),
        ),
      );
      if (!systemTransform) return false;

      return Boolean(systemTransform.groupBy?.includes("eventName"));
    }, [block, selectedIdentifier]);

    const updateSwitchSystemTransforms = useCallback(
      (value: string, distributionByEvent: boolean) => {
        /**
         * @note Updates concerning the standard transforms are made in LayerContent.tsx
         */

        if (!block) return;

        const kind = getAvailableResourceKind(availableResources, value);

        /**
         * UPDATE TRANSFORMS
         * ======================================================================
         */

        const systemTransform = (block.data.transforms ?? []).find(t =>
          t.filter?.every(
            filter =>
              filter.column === "faultStatus" ||
              (filter.column === "systemId" &&
                filter.value.includes(kind === "SYSTEM" ? value : null)),
          ),
        );
        if (!systemTransform) return;

        systemTransform.filter = systemTransform?.filter?.map(f => {
          if (f.column === "faultStatus") {
            f.value = distributionByEvent
              ? [
                  "CONFIRMED",
                  "NON_MODELLED",
                  "SUSPECTED",
                  // "BACK_TO_BE_ANALYSED",
                  "TO_BE_ANALYSED",
                ]
              : ["CONFIRMED", "NON_MODELLED"];
          }
          return f;
        });
        const eventName = distributionByEvent ? ["eventName"] : [];
        systemTransform.groupBy = ["systemId", "date", ...eventName];

        const updatedTransforms = [...(block.data.transforms ?? [])].map(t => {
          if (t === systemTransform) return systemTransform;
          return t;
        });

        const updatedBlock: BlockDiagramModelT = {
          ...block,
          config: distributionByEvent
            ? {
                ...block.config,
                layers: block.config.layers.map(l => {
                  if (l !== annotation) return l;
                  return {
                    ...l,
                    evolutions: [],
                  };
                }),
              }
            : block.config,
          data: {
            ...block.data,
            transforms: updatedTransforms,
          },
        };

        void editor?.updateBlock(updatedBlock);
      },
      [block, editor, annotation, availableResources],
    );

    const handleSwitchDistributionByEvent = useCallback(() => {
      updateSwitchSystemTransforms(selectedIdentifier ?? "", !distributionByEventMode);
    }, [selectedIdentifier, distributionByEventMode, updateSwitchSystemTransforms]);

    const updateColumn = useCallback(
      (
        e: React.ChangeEvent<HTMLInputElement>,
        key: string,
        performanceIndicator?: string | undefined,
      ) => {
        if (!block) return;

        const identifier = selectedIdentifier ?? "";
        const kind = getAvailableResourceKind(availableResources, identifier);

        if (!kind || !identifier) return;

        const isUncheckingActivePower =
          performanceIndicator === activePowerQuantity?.name && !e.target.checked;
        let updatedTransforms = [...(block.data.transforms ?? [])];
        if (isUncheckingActivePower && distributionByEventMode && block.data.period) {
          handleSwitchDistributionByEvent();

          const findTransformByValue = updatedTransforms.find(t =>
            t?.filter?.some(f => f.value.includes(identifier)),
          );
          if (findTransformByValue) {
            updateFaultStatus(findTransformByValue, ["CONFIRMED", "NON_MODELLED"]);

            updatedTransforms = updatedTransforms.filter(
              t =>
                !(
                  t.groupBy?.includes("eventName") &&
                  t.filter?.some(f => f.value.includes(identifier))
                ),
            );
          }
        }

        const updatedLayers = block.config.layers.map(layer => {
          if (layer !== annotation) return layer;

          const updatedEncoding = { identifier, column: key, quantity: performanceIndicator };
          const updatedEncodings = e.target.checked
            ? uniqBy(
                [...layer.encodings, updatedEncoding],
                item => `${item.column}-${item.quantity}`,
              )
            : layer.encodings.filter(encoding => {
                if (encoding.column === updatedEncoding.column) {
                  return encoding.quantity !== updatedEncoding.quantity;
                }
                return encoding.column !== updatedEncoding.column;
              });

          if (
            !e.target.checked &&
            updatedEncodings.length === 1 &&
            ["faultStatus", "validity"].includes(updatedEncodings[0]?.column ?? "")
          ) {
            updatedEncodings.length = 0;
          }

          const updatedEvolutions = e.target.checked
            ? layer.evolutions
            : layer.evolutions.filter(
                evo =>
                  !(
                    evo.identifier === updatedEncoding.identifier &&
                    evo.column === updatedEncoding.column &&
                    (evo.quantity ?? null) === (updatedEncoding.quantity ?? null)
                  ),
              );

          return {
            ...layer,
            encodings: updatedEncodings,
            evolutions: updatedEvolutions,
          };
        });

        void editor?.updateBlock({
          ...block,
          data: {
            ...block.data,
            transforms: updatedTransforms,
          },
          config: {
            ...block.config,
            layers: updatedLayers,
          },
        });
      },
      [
        block,
        selectedIdentifier,
        availableResources,
        editor,
        annotation,
        activePowerQuantity,
        distributionByEventMode,
        handleSwitchDistributionByEvent,
      ],
    );

    const updateSystemFilters = useCallback(
      (identifier: string | null, option: React.ChangeEvent<HTMLSelectElement>) => {
        if (!block) return;
        if (!identifier || !Array.isArray(block.data.transforms)) return;

        const filterValue = Array.from(option.currentTarget.selectedOptions).map(
          ({ value }) => value,
        );
        const updatedTransforms = block.data.transforms.map(t => {
          if (t.filter?.some(f => f.value.includes(identifier))) {
            return {
              ...t,
              filter: t.filter.map(f => {
                if (f.column !== "faultStatus") return f;
                return { ...f, value: filterValue };
              }),
            };
          }
          return t;
        });

        void editor?.updateBlock({
          ...block,
          data: {
            ...block.data,
            transforms: updatedTransforms,
          },
        });
      },
      [block, editor],
    );

    const disabledDescription = useMemo(
      () =>
        !annotation.showLabel ||
        isSelectedIdentifierManualFailure ||
        isSelectedSystemKind ||
        isSelectedUnexplainedLosses,
      [
        annotation.showLabel,
        isSelectedIdentifierManualFailure,
        isSelectedSystemKind,
        isSelectedUnexplainedLosses,
      ],
    );

    const updateFaultStatus = (
      transform: NonNullable<BlockDiagramModelT["data"]["transforms"]>[number],
      status: string[],
    ) => {
      const filterIndexToUpdate = transform.filter?.findIndex(f => f.column === "faultStatus");
      if (filterIndexToUpdate === undefined || filterIndexToUpdate === -1) return;

      if (transform && transform.filter && transform.filter[filterIndexToUpdate]) {
        transform.filter[filterIndexToUpdate].value = status;
      }
    };

    const firstPerformanceIndicator = useMemo(
      () => availableResources.performanceIndicators[0]?.name ?? null,
      [availableResources.performanceIndicators],
    );

    return (
      <div className={diagramEditorAnnotationSettings}>
        <Text type="body1" className={diagramEditorAnnotationSettingsTitle}>
          {t("project.dashboard.editor.gui.section.annotation_settings.title")}
        </Text>

        <Checkbox
          name="showLabel"
          checked={annotation.showLabel}
          onChange={updateShowInfo}
          className={diagramEditorAnnotationLabelOption}
        >
          {t("project.dashboard.editor.gui.section.annotation_settings.show_label")}
        </Checkbox>
        <Checkbox
          name="showDescription"
          checked={disabledDescription ? false : annotation.showDescription}
          onChange={updateShowInfo}
          className={diagramEditorAnnotationLabelOption}
          disabled={disabledDescription}
        >
          {t("project.dashboard.editor.gui.section.annotation_settings.show_description")}
        </Checkbox>
        {annotation.showLabel && (
          <AnnotationLayerNameSettings
            projectId={projectId}
            block={block}
            annotation={annotation}
            placeholder={
              selectedIdentifier === AGGREGATE_UNEXPLAINED_LOSSES
                ? t("charts.legend.unexplained_losses", translations)
                : selectedIdentifier === AGGREGATE_NO_SYSTEM
                  ? t("project.dashboard.editor.gui.no_system")
                  : (availableResources.fault.identifiers.find(i => i.name === selectedIdentifier)
                      ?.displayName ??
                    selectedIdentifier ??
                    "")
            }
          />
        )}
        <AnnotationLayerContentSettings
          projectId={projectId}
          annotation={annotation}
          block={block}
        />
        {currentKind === "QUANTITY" && (
          <div className={diagramEditorAnnotationValues}>
            <Text type="body1">
              {t("project.dashboard.editor.gui.section.annotation_settings.value")}
            </Text>
            {QUANTITY_COLUMN.filter(c => c !== "validity").map(column => (
              <Checkbox
                key={column}
                overlay={
                  isModelOutputColumn(column) && !isSelectedIdentifierUsedForSimulationOutputs
                    ? t(
                        "project.dashboard.editor.gui.section.annotation_settings.disabled_expected_gap",
                      )
                    : column === "std" && isSelectedIdentifierFormulaQuantity
                      ? t("project.dashboard.editor.gui.section.annotation_settings.disabled_std")
                      : column === "normalizedGap" &&
                          availableResources.observables.every(o => o.name !== selectedIdentifier)
                        ? t(
                            "project.dashboard.editor.gui.section.annotation_settings.disabled_normalized_gap",
                          )
                        : undefined
                }
                disabled={
                  (isModelOutputColumn(column) && !isSelectedIdentifierUsedForSimulationOutputs) ||
                  (column === "std" && isSelectedIdentifierFormulaQuantity) ||
                  (column === "normalizedGap" &&
                    availableResources.observables.every(o => o.name !== selectedIdentifier))
                }
                checked={
                  (isModelOutputColumn(column) && !isSelectedIdentifierUsedForSimulationOutputs) ||
                  (column === "std" && isSelectedIdentifierFormulaQuantity)
                    ? false
                    : Boolean(annotation.encodings.find(e => e.column === column))
                }
                onChange={e => updateColumn(e, column)}
              >
                {t("project.dashboard.resource.column.annotation", column)}
              </Checkbox>
            ))}
            {availableResources.performanceIndicators.map(pi => (
              <Checkbox
                overlay={
                  isSelectedIdentifierUsedForExternalGap
                    ? undefined
                    : t(
                        "project.dashboard.editor.gui.section.annotation_settings.disabled_external_impacts",
                      )
                }
                disabled={!isSelectedIdentifierUsedForExternalGap}
                key={pi?.name}
                checked={annotation?.encodings.some(
                  e => e.column === "externalImpacts" && e.quantity === pi?.name,
                )}
                onChange={e => updateColumn(e, "externalImpacts", pi?.name)}
              >
                {t("project.dashboard.resource.column.annotation", "externalImpacts")} {pi?.name}
              </Checkbox>
            ))}
          </div>
        )}
        {currentKind === "FAULT" && (
          <div className={diagramEditorAnnotationValues}>
            <Text type="body1">
              {t("project.dashboard.editor.gui.section.annotation_settings.value")}
            </Text>
            {FAULT_COLUMN.filter(c => c !== "faultStatus").map(column => (
              <Checkbox
                key={column}
                disabled={column === "real" && isSelectedIdentifierManualFailure}
                checked={
                  column === "real" && isSelectedIdentifierManualFailure
                    ? false
                    : annotation?.encodings.some(e => e.column === column)
                }
                onChange={e => updateColumn(e, column)}
                overlay={
                  column === "real" && isSelectedIdentifierManualFailure
                    ? t(
                        "project.dashboard.editor.gui.section.annotation_settings.disabled_amplitude",
                      )
                    : undefined
                }
              >
                {t("project.dashboard.resource.column.annotation", column)}
              </Checkbox>
            ))}
            {availableResources?.performanceIndicators.map(pi => (
              <Checkbox
                key={pi?.name}
                checked={annotation?.encodings.some(
                  e => e.column === "quantityImpacts" && e.quantity === pi?.name,
                )}
                onChange={e => updateColumn(e, "quantityImpacts", pi?.name)}
              >
                {t("project.dashboard.resource.column.annotation", "quantityImpacts")} {pi?.name}
              </Checkbox>
            ))}
          </div>
        )}
        {currentKind === "SYSTEM" && (
          <div className={diagramEditorAnnotationValuesForPI}>
            <Text type="body1SemiBold">
              {t("project.dashboard.editor.gui.section.annotation_settings.system_value")}
            </Text>

            <div className={diagramEditorAnnotationValues}>
              {availableResources?.performanceIndicators.map(pi => (
                <Checkbox
                  key={pi?.name}
                  checked={annotation?.encodings.some(
                    e => e.column === "systemImpact" && e.quantity === pi?.name,
                  )}
                  onChange={e => {
                    updateColumn(e, "systemImpact", pi?.name);
                  }}
                >
                  {pi?.name}
                </Checkbox>
              ))}
              <div className={diagramEditorDistributionByEvent}>
                <TooltipV2
                  placement="bottom"
                  overlay={
                    hasActivePowerQuantitySelected ? (
                      ""
                    ) : (
                      <OverlayWrapper
                        text={t(
                          "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.tooltip.disabled",
                        )}
                      />
                    )
                  }
                >
                  <div>
                    <Switch
                      position="right"
                      justify
                      checked={distributionByEventMode}
                      onChange={handleSwitchDistributionByEvent}
                      disabled={!hasActivePowerQuantitySelected}
                    >
                      <span>
                        {t(
                          "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.toggle",
                        )}
                      </span>
                    </Switch>
                  </div>
                </TooltipV2>
                {distributionByEventMode && (
                  <Text type="body2">
                    {t(
                      "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.description",
                      firstPerformanceIndicator ?? "",
                    )}
                  </Text>
                )}
              </div>

              <div className={diagramEditorAnnoationFilter}>
                <Select
                  label={t(
                    "project.dashboard.editor.gui.section.annotation_settings.system_filter",
                  )}
                  options={INCLUDED_FAULTS}
                  multiple
                  revertable={false}
                  value={selectedSystemFaultStatus}
                  defaultValue={["CONFIRMED", "NON_MODELLED"]}
                  onChange={e => updateSystemFilters(selectedIdentifier, e)}
                  prompt={t("common.select")}
                  className={diagramEditorIdentifierSelector}
                  clearable={false}
                  disabled={distributionByEventMode}
                />
              </div>
            </div>
          </div>
        )}

        {currentKind === "AGGREGATE" && (
          <>
            <div className={diagramEditorAnnotationValues}>
              <Text type="body1">
                {t("project.dashboard.editor.gui.section.annotation_settings.value")}
              </Text>
              {availableResources?.performanceIndicators.map(pi => (
                <Checkbox
                  key={pi?.name}
                  checked={annotation?.encodings.some(
                    e => e.column === "unexplainedLosses" && e.quantity === pi?.name,
                  )}
                  onChange={e => updateColumn(e, "unexplainedLosses", pi?.name)}
                >
                  {pi?.name}
                </Checkbox>
              ))}
            </div>
          </>
        )}
        <AnnotationLayerEvolutionsSettings
          block={block}
          annotation={annotation}
          encodingOptions={encodingOptions}
        />
        <AnnotationLayerThresholdsSettings
          block={block}
          annotation={annotation}
          encodingOptions={encodingOptions}
        />
      </div>
    );
  },
);

//#region CSS
const diagramEditorAnnotationSettings = css`
  > * {
    width: 100%;
  }
`;

const diagramEditorAnnotationSettingsTitle = css`
  color: ${colors.carbon800};
`;

const diagramEditorIdentifierSelector = css`
  margin-top: ${spacings.m};
`;

const diagramEditorAnnotationValues = css`
  color: ${colors.carbon800};
  padding-left: ${spacings.m};
  margin-top: ${spacings.xs};
  border-left: 1px solid ${colors.titanium200};
`;

const diagramEditorAnnoationFilter = css`
  &,
  > * {
    width: 100%;
  }
`;

const diagramEditorAnnotationLabelOption = css`
  margin-top: ${spacings.xs};
`;

const diagramEditorAnnotationValuesForPI = css`
  padding-top: ${spacings.m};
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};

  & > *:nth-child(1) {
    color: ${colors.carbon800};
  }
`;

const diagramEditorDistributionByEvent = css`
  padding-top: ${spacings.s};
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
`;
//#endregion CSS
